import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Pipe that highlights occurrences of a search term inside a text string.
 * The matched results are wrapped with a `span` element with a specific highlighting class.
 *
 * This pipe also sanitizes the resulting HTML to prevent XSS attacks.
 *
 * To use this pipe, simply provide the text and the search term:
 *
 * ```html
 * {{ text | highlight:search }}
 * ```
 */
@Pipe({
  name: 'highlight'
})
export class HighlightPipe implements PipeTransform {

  /**
   * Constructor to inject Angular's `DomSanitizer` for sanitizing the output HTML
   * to ensure it is safe to use in templates.
   *
   * @param sanitizer - Instance of `DomSanitizer` used for sanitizing HTML.
   */
  constructor(
    private readonly sanitizer: DomSanitizer
  ) { }

  /**
   * Transforms the text by highlighting all occurrences of the search term.
   *
   * - Matches are wrapped in a `<span>` with a `bg-yellow-200` class for highlighting.
   * - The result is sanitized using Angular's `DomSanitizer` to prevent XSS attacks.
   *
   * @param text - The input string where the search term should be highlighted.
   *               Can be `null` or `undefined`, in which case it returns an empty string.
   * @param search - The search term to find within the text. If empty or invalid, no highlighting occurs.
   *
   * @returns A `SafeHtml` object that contains the transformed and sanitized HTML with highlighted terms.
   */
  transform(text: string | null | undefined, search: string): SafeHtml {
    if (!text || !search || search.length < 1) {
      return text ?? '';
    }

    // Escape the search string for use in a regular expression
    const searchRegex = new RegExp(search.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');

    // Replace occurrences with highlighted text
    const result = text.replace(searchRegex, match => `<span class="bg-yellow-200">${match}</span>`);

    // Return the sanitized HTML to prevent XSS attacks
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }
}
